<template>
  <AttrGroup title="填充">
    <div v-if="deviceType == 'O61' || deviceType == 'Q61'" class="mb-2.5">
      <AttrSelect
        label="选择文件数量"
        :value="riveFileCounts"
        :options="options"
        @change="onChangeRiveFileCounts"
      />
    </div>
    <UploadDirectory
      :label="'Rive文件'"
      :dirname="shape.attrs.riveDiranme"
      :disabled="!!attrDisabled"
      :tooltip="
        androidWatcfaceOS != 'not-android-device'
          ? riveFileCounts == 2
            ? '适用于轻智能'
            : '适用于智能&轻智能'
          : ''
      "
      @upload="() => onAdd('normal')"
      @delete="() => onDelete('normal')"
    />

    <div v-if="deviceType == 'O61' || deviceType == 'Q61'" class="mt-2.5">
      <UploadDirectory
        :label="'Rive高级文件'"
        :dirname="shape.attrs.riveDiranmeExt"
        :tooltip="riveFileCounts == 2 ? '适用于智能' : ''"
        :disabled="!!attrDisabled || (shape.attrs.riveFileCounts ?? 1) == 1"
        @upload="() => onAdd('ext')"
        @delete="() => onDelete('ext')"
      />
      <template v-if="androidWatcfaceOS == 'vela'">
        <AdvancedFeatureMessage
          :message="'智能类型为轻智能时，无法使用Rive高级文件！'"
        />
      </template>
    </div>
  </AttrGroup>

  <!-- 预览图 -->
  <ShapePreviewImage
    class="mt-2.5"
    :shape="shape"
    preview-key="rivePreview"
    :multi-color="true"
  />

  <AttrGroup v-if="androidWatcfaceOS != 'not-android-device'" title="画布显示">
    <AttrSwitch
      label="Rive文件"
      :value="(shape.attrs.riveToShow ?? 'normal') != 'ext'"
      @change="changeRiveToShow"
    />
    <AttrSwitch
      class="mt-2.5"
      label="Rive高级文件"
      :value="(shape.attrs.riveToShow ?? 'normal') == 'ext'"
      @change="changeRiveToShow"
    />
  </AttrGroup>

  <AttrGroup title="Rive数据源" class="mt-2.5">
    <div class="px-2.5">
      <div v-if="sourcekeys.length" class="">
        <div class="text-sm text-primary mb-1">可在“预览区”预览数据源效果</div>
        <div class="grid grid-cols-1 gap-y-2 bg-black-card p-2.5 rounded-xl">
          <div v-for="(item, index) in sourcekeys" :key="item.key">
            {{ index + 1 }}: {{ item.label }}
          </div>
        </div>
      </div>
      <div v-else class="opacity-70 text-center">
        <!-- 处理图层是否隐藏 -->
        <div v-if="!shape.attrs._visibility">
          图层隐藏，请先在“图层”面板中显示图层后，查看。
        </div>
        <div v-else>Rive文件中未配置数据源或数据源名称不规范。</div>
      </div>
    </div>
  </AttrGroup>

  <!-- 表盘整体配色 -->
  <watchface-colors class="mt-2.5" />
  <!-- 提示 -->
  <div class="px-2.5 mt-2.5">
    <div class="flex mt-1">
      <div class="w-6 flex-shrink-0">1：</div>
      <div class="opacity-75">
        请在rive文件中用
        <span class="text-green-400 font-bold">themeColor</span>
        或
        <span class="text-green-400 font-bold">color</span>
        字段设置主题色；
      </div>
    </div>
    <div class="flex mt-1">
      <div class="w-6 flex-shrink-0">2：</div>
      <div class="opacity-75">色值顺序需要和表盘色值顺序一致；</div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import {
  RiveFileType,
  useEmitterListener,
  useShapeRive,
  useWatchfaceAndroid
} from '@/hooks';
import { dataSourceWholeItems } from '@/resources/data-source';
import { KonvaUtils } from '@/services/konva/konva.utils';
import { Shape } from '@/services/shape';
import { useEditorStore } from '@/store/use-editor-store';
import { useWatchfaceStore } from '@/store/use-watchface-store';
import { EventType } from '@/utils/emitter.utils';
import { computed, onMounted, ref } from 'vue';
import { PropType } from 'vue';

const props = defineProps({
  shape: {
    type: Object as PropType<Shape>,
    required: true
  }
});

const editorStore = useEditorStore();
const attrDisabled = computed(() => editorStore.attrDisabled);
const watchStore = useWatchfaceStore();
const deviceType = computed(() => watchStore.deviceType);

const { androidWatcfaceOS } = useWatchfaceAndroid();

const { pickThenUploadRiveDir, removeRiveFile } = useShapeRive();

const riveFileCounts = computed(() => props.shape.attrs.riveFileCounts ?? 1);
const onDelete = (type: RiveFileType) => {
  const shape = props.shape;
  removeRiveFile(shape, type);
};

const onAdd = async (type: RiveFileType) => {
  const shape = props.shape;
  await pickThenUploadRiveDir(shape, type);
};

// 获取数据源
const sourcekeys = ref<
  {
    label: string;
    key: string;
  }[]
>([]);
const resetSourceKeys = () => {
  const runtime = KonvaUtils.getRuntimData(props.shape.id);
  const keys = runtime?.riveSourceKeys ?? [];

  sourcekeys.value = keys.map((key) => {
    const label =
      dataSourceWholeItems.find((item) => item.key === key)?.label ?? '';
    return {
      label,
      key
    };
  });
};
onMounted(() => resetSourceKeys());
useEmitterListener(EventType['renderer:rive-set-inputs'], resetSourceKeys);

const changeRiveToShow = () => {
  const shape = props.shape;
  const type = (shape.attrs.riveToShow ?? 'normal') == 'ext' ? 'normal' : 'ext';
  shape.setAttrs({
    riveToShow: type
  });
};

const options = [
  { label: '1个rive文件', value: '1' },
  { label: '2个rive文件', value: '2' }
];

const onChangeRiveFileCounts = (val: number) => {
  props.shape.setAttrs({ riveFileCounts: val });
};
</script>
